import { differenceInHours, isToday } from 'date-fns';
import _round from 'lodash/round';

import { formatNumberCompact, locale } from '..';

interface GetExpirationMessageInput {
    /**
     * The current date; defaults to the time the function is run
     */
    currentDate?: Date;
    /**
     * The expiration date
     */
    expirationDate: Date;
    /**
     * Verb that describes a past tense action, i.e. 'expired'
     */
    verbPast: string;
    /**
     * Verb that describes a present tense action, i.e. 'expires'
     */
    verbPresent: string;
}

/**
 * Returns a message that describes an expiration date. See unit tests for specific examples:
 * {@link packages/utils/src/messages/tests/getExpirationMessage.test.ts}
 * @param input - {@link GetExpirationMessageInput}
 * @returns a string like "expires in 3 days"; the exact result depends on some more detailed logic
 */
export const getExpirationMessage = ({
    currentDate = new Date(),
    expirationDate,
    verbPast,
    verbPresent,
}: GetExpirationMessageInput): string => {
    if (isToday(expirationDate) && expirationDate < currentDate) {
        // If the expiration date is in the past and happened today, return "expired today"
        return `${verbPast} ${locale.enUS.EXPIRATION_DATE.TODAY}`;
    }

    const expiresInHours =
        expirationDate !== null
            ? differenceInHours(expirationDate, currentDate)
            : 24 * 14;

    // Round days up to the nearest full day
    const expiresInDays = _round(expiresInHours / 24, 0);

    // Round months to 1 decimal point
    const expiresInMonths = _round(expiresInDays / 30, 1);

    if (expiresInHours < 0) {
        // If the expiration date is in the past and happened before today, return a message like:
        // "expired on Mon, May 17, 2021"
        return `${verbPast} ${
            locale.enUS.EXPIRATION_DATE.ON
        } ${expirationDate.toLocaleDateString('en', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
        })}`;
    }

    // If the expiration date is one hour away, return "expires in one hour"
    if (expiresInHours === 1) {
        return `${verbPresent} ${locale.enUS.EXPIRATION_DATE.IN} ${locale.enUS.EXPIRATION_DATE.ONE_HOUR}`;
    }

    // If the expiration date is 48 hours away or less, return "expires in n hours"
    if (expiresInHours <= 48) {
        return `${verbPresent} ${locale.enUS.EXPIRATION_DATE.IN} ${expiresInHours} ${locale.enUS.EXPIRATION_DATE.HOURS}`;
    }

    // If the expiration date is 30 days away or less, return "expires in n days"
    if (expiresInDays <= 30) {
        return `${verbPresent} ${locale.enUS.EXPIRATION_DATE.IN} ${expiresInDays} ${locale.enUS.EXPIRATION_DATE.DAYS}`;
    }

    // Otherwise, return "expires in n months"
    return `${verbPresent} ${
        locale.enUS.EXPIRATION_DATE.IN
    } ${formatNumberCompact(expiresInMonths)} ${
        locale.enUS.EXPIRATION_DATE.MONTHS
    }`;
};
